import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { Sparkles, Send, Loader2, Plus, MessagesSquare, Trash2, ArrowUpRight, Handshake, Lock, Copy, Check, ChevronLeft, Pin, Pencil, BellPlus, GitCompareArrows, Car, ListOrdered } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { useAnalyst } from "@/context/AnalystContext";
import { useAuth } from "@/context/AuthContext";
import { copyText } from "@/lib/clipboard";
import { ptMeta } from "@/lib/propertyTypes";

const money = (n) =>
  typeof n === "number" && !isNaN(n)
    ? n.toLocaleString("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 0 })
    : "—";

const SUGGESTED = [
  "Homes under $5 million",
  "Low-mileage Porsches for sale",
  "How is the Aspen luxury market?",
  "Best-value estates in Beverly Hills",
];

function MetaLine({ card }) {
  const m = card.meta || {};
  if (card.item_type === "vehicle") {
    return [m.year, m.miles != null ? `${Number(m.miles).toLocaleString()} mi` : null].filter(Boolean).join(" · ");
  }
  return [m.beds != null ? `${m.beds} bd` : null, m.baths != null ? `${m.baths} ba` : null,
    m.sqft ? `${Number(m.sqft).toLocaleString()} sqft` : null].filter(Boolean).join(" · ");
}

function ResultThumb({ l }) {
  if (l.image) return <img src={l.image} alt="" className="w-14 h-14 rounded-lg object-cover shrink-0" />;
  if (l.item_type === "vehicle") {
    return (
      <div className="w-14 h-14 rounded-lg shrink-0 flex items-center justify-center border text-rose-400 bg-rose-500/10 border-rose-500/30" data-testid="chat-result-icon">
        <Car className="w-6 h-6" strokeWidth={1.5} />
      </div>
    );
  }
  const meta = ptMeta(l.property_type);
  const Icon = meta.icon;
  return (
    <div className={`w-14 h-14 rounded-lg shrink-0 flex items-center justify-center border ${meta.cls}`} data-testid="chat-result-icon" title={l.property_type || "Property"}>
      <Icon className="w-6 h-6" strokeWidth={1.5} />
    </div>
  );
}

function ResultChips({ results, onNavigate, pins, onPin }) {
  const listings = results?.listings || [];
  if (!listings.length) return null;
  const slotOf = (id) => (pins?.A?.item_id === id ? "A" : pins?.B?.item_id === id ? "B" : null);
  return (
    <div className="mt-3 space-y-1.5" data-testid="chat-results">
      {listings.map((l) => {
        const slot = slotOf(l.id);
        return (
          <div key={l.id} data-testid="chat-result-row"
            className={`flex items-center gap-2 border rounded-xl p-2 bg-surface/60 transition-colors ${slot ? "border-accent" : "border-border"}`}>
            <Link to={l.item_type === "vehicle" ? `/vehicles/${l.id}` : `/homes/${l.id}`}
              onClick={onNavigate} data-testid="chat-result-item"
              className="flex items-center gap-3 flex-1 min-w-0 group">
              <ResultThumb l={l} />
              <div className="min-w-0 flex-1">
                <div className="text-sm font-medium truncate">{l.title}</div>
                <div className="font-mono-data text-[11px] text-muted-foreground truncate">{l.subtitle}</div>
                <div className="text-[11px] text-muted-foreground truncate">{MetaLine({ card: l })}</div>
              </div>
              <div className="flex items-center gap-1 shrink-0">
                <span className="font-mono-data text-xs font-medium">{money(l.price)}</span>
                <ArrowUpRight className="w-3.5 h-3.5 text-muted-foreground group-hover:text-accent transition-colors" />
              </div>
            </Link>
            <div className="flex items-center gap-1 shrink-0" title="Pin to compare">
              {["A", "B"].map((s) => {
                const item = { item_type: l.item_type, item_id: l.id, title: l.title, image: l.image, subtitle: l.subtitle, price: l.price };
                const active = slot === s;
                return (
                  <button key={s} type="button" onClick={() => onPin?.(s, item)}
                    data-testid={`chat-pin-${s}`} aria-pressed={active} title={`Pin to ${s}`}
                    className={`w-7 h-7 rounded-full border text-[11px] font-mono-data font-semibold flex items-center justify-center transition-colors ${active ? "bg-accent border-accent text-background" : "border-border text-muted-foreground hover:border-accent/60 hover:text-foreground"}`}>
                    {s}
                  </button>
                );
              })}
            </div>
          </div>
        );
      })}
    </div>
  );
}

const CONF_STYLE = {
  high: "text-emerald-500 border-emerald-500/40",
  medium: "text-amber-500 border-amber-500/40",
  low: "text-muted-foreground border-border",
};

function NegotiationCard({ neg }) {
  const [copied, setCopied] = useState(false);
  const copy = async () => {
    const ok = await copyText(neg.message || "");
    if (ok) { setCopied(true); toast.success("Message copied"); setTimeout(() => setCopied(false), 2000); }
    else toast.error("Couldn't copy — select and copy manually");
  };
  const e = neg.offer_estimate || {};
  return (
    <div className="mt-3 border border-border rounded-xl p-3.5 space-y-3 bg-surface/60" data-testid="chat-negotiation">
      <div className="flex items-center gap-2">
        <Handshake className="w-4 h-4 text-accent" />
        <div className="font-display font-semibold text-sm truncate flex-1">{neg.title}</div>
        <span className={`font-mono-data text-[10px] uppercase tracking-wider border rounded-full px-2 py-0.5 ${CONF_STYLE[neg.confidence] || CONF_STYLE.medium}`}>
          {neg.confidence}
        </span>
      </div>
      <div className="grid grid-cols-3 gap-2 text-center" data-testid="chat-negotiation-offer">
        {[["Opening", e.low], ["Target", e.target], ["Walk-away", e.high]].map(([label, val], i) => (
          <div key={label} className={`rounded-lg py-2 ${i === 1 ? "bg-accent/10 border border-accent/30" : "bg-secondary"}`}>
            <div className="font-mono-data text-[9px] uppercase tracking-wider text-muted-foreground">{label}</div>
            <div className={`text-sm font-semibold ${i === 1 ? "text-accent" : ""}`}>{money(val)}</div>
          </div>
        ))}
      </div>
      {e.rationale && <p className="text-xs text-muted-foreground leading-relaxed">{e.rationale}</p>}
      {neg.negotiation_points?.length > 0 && (
        <ul className="space-y-1.5">
          {neg.negotiation_points.map((p, i) => (
            <li key={i} className="flex gap-2 text-sm leading-relaxed"><span className="text-accent font-semibold shrink-0">{i + 1}.</span><span>{p}</span></li>
          ))}
        </ul>
      )}
      {neg.message && (
        <div>
          <div className="flex items-center justify-between mb-1.5">
            <div className="font-mono-data text-[10px] uppercase tracking-wider text-muted-foreground">Negotiation message</div>
            <button onClick={copy} data-testid="chat-negotiation-copy" className="flex items-center gap-1.5 font-mono-data text-[10px] uppercase tracking-wider text-accent hover:opacity-80">
              {copied ? <Check className="w-3 h-3" /> : <Copy className="w-3 h-3" />}{copied ? "Copied" : "Copy"}
            </button>
          </div>
          <div className="bg-secondary text-secondary-foreground text-sm rounded-xl px-3.5 py-3 leading-relaxed whitespace-pre-wrap">{neg.message}</div>
        </div>
      )}
      <p className="text-[10px] text-muted-foreground/70 italic">Estimated negotiation range from verified Vaelith data — not an appraisal.</p>
    </div>
  );
}

function fmtCell(val, fmt) {
  if (val === null || val === undefined || val === "") return "—";
  if (fmt === "currency") return money(val);
  if (fmt === "int") return Number(val).toLocaleString();
  if (fmt === "pct") return `${val > 0 ? "+" : ""}${val}%`;
  return String(val);
}

function ComparisonCard({ cmp }) {
  const [a, b] = cmp.items || [];
  if (!a || !b) return null;
  // Buyer-perspective winner per metric (lower/higher better); null = don't highlight (e.g. price).
  const betterIdx = (label, v0, v1) => {
    const n0 = Number(v0), n1 = Number(v1);
    if (!isFinite(n0) || !isFinite(n1) || n0 === n1) return null;
    if (["$ / Sq Ft", "Mileage", "Days on Market"].includes(label)) return n0 < n1 ? 0 : 1;
    if (["Size (sq ft)", "Beds", "Baths", "Year"].includes(label)) return n0 > n1 ? 0 : 1;
    return null;
  };
  const Head = ({ c, align }) => (
    <Link to={c.item_type === "vehicle" ? `/vehicles/${c.id}` : `/homes/${c.id}`}
      className={`flex flex-col gap-1 p-3 group ${align === "right" ? "items-end text-right" : "items-start"}`} data-testid="chat-compare-head">
      {c.image
        ? <img src={c.image} alt="" className="w-full h-20 rounded-lg object-cover" />
        : <div className="w-full h-20 rounded-lg bg-secondary" />}
      <div className="text-xs font-medium leading-tight group-hover:text-accent transition-colors line-clamp-2">{c.title}</div>
      {c.subtitle && <div className="font-mono-data text-[10px] text-muted-foreground truncate w-full">{c.subtitle}</div>}
      <div className="font-mono-data text-sm font-semibold">{money(c.price)}</div>
    </Link>
  );
  return (
    <div className="mt-3 border border-border rounded-xl overflow-hidden bg-surface/60" data-testid="chat-comparison">
      <div className="px-3 py-2 flex items-center gap-1.5 font-mono-data text-[10px] uppercase tracking-wider text-muted-foreground border-b border-border/60">
        <GitCompareArrows className="w-3.5 h-3.5 text-accent" /> Side-by-side comparison
      </div>
      <div className="grid grid-cols-[1fr_auto_1fr] items-stretch">
        <Head c={a} align="left" />
        <div className="flex items-center justify-center px-1">
          <span className="font-display font-bold text-[11px] text-muted-foreground bg-background border border-border rounded-full w-8 h-8 flex items-center justify-center shrink-0">VS</span>
        </div>
        <Head c={b} align="right" />
      </div>
      <div className="divide-y divide-border/60 border-t border-border/60">
        {(cmp.rows || []).map((r) => {
          const bi = betterIdx(r.label, r.values?.[0], r.values?.[1]);
          return (
            <div key={r.label} className="grid grid-cols-[1fr_auto_1fr] items-center text-sm">
              <div className={`py-1.5 px-3 text-right font-medium ${bi === 0 ? "text-emerald-500" : ""}`}>{fmtCell(r.values?.[0], r.fmt)}</div>
              <div className="py-1.5 px-2 text-center text-[9px] font-mono-data uppercase tracking-wider text-muted-foreground/70 whitespace-nowrap">{r.label}</div>
              <div className={`py-1.5 px-3 text-left font-medium ${bi === 1 ? "text-emerald-500" : ""}`}>{fmtCell(r.values?.[1], r.fmt)}</div>
            </div>
          );
        })}
      </div>
      {cmp.note && <p className="text-[10px] text-muted-foreground/70 px-3 py-2 italic border-t border-border/60">{cmp.note}</p>}
    </div>
  );
}

function AlertButton({ search }) {
  const { saveAlert } = useAnalyst() || {};
  const [state, setState] = useState("idle"); // idle | saving | saved
  const save = async () => {
    setState("saving");
    const r = await saveAlert(search);
    if (r?.ok) { setState("saved"); toast.success(`Alert saved — we'll ping you on new matches for “${r.label}”`); }
    else { setState("idle"); toast.error("Couldn't save the alert. Please try again."); }
  };
  if (!search) return null;
  return (
    <button onClick={save} disabled={state !== "idle"} data-testid="chat-save-alert"
      className="mt-2 inline-flex items-center gap-1.5 text-xs font-mono-data uppercase tracking-wider px-3 py-1.5 rounded-full border border-accent/40 text-accent hover:bg-accent/10 transition-colors disabled:opacity-60">
      {state === "saved" ? <Check className="w-3.5 h-3.5" /> : <BellPlus className="w-3.5 h-3.5" />}
      {state === "saved" ? "Alert saved" : state === "saving" ? "Saving…" : "Save as alert"}
    </button>
  );
}

function CompareTray({ pins, clearPin, clearPins, onCompare }) {
  const a = pins?.A, b = pins?.B;
  if (!a && !b) return null;
  const bothSame = a && b && a.item_type === b.item_type;
  const typeMismatch = a && b && a.item_type !== b.item_type;
  const Slot = ({ s, item }) => (
    <div className="flex-1 min-w-0 flex items-center gap-1.5">
      <span className="w-5 h-5 rounded-full bg-accent/15 border border-accent/40 text-accent text-[10px] font-mono-data font-semibold flex items-center justify-center shrink-0">{s}</span>
      {item ? (
        <>
          <span className="truncate text-[11px] text-foreground/90">{item.title || "listing"}</span>
          <button type="button" onClick={() => clearPin(s)} data-testid={`chat-clear-pin-${s}`} className="text-muted-foreground hover:text-foreground shrink-0" title={`Clear ${s}`}>✕</button>
        </>
      ) : (
        <span className="truncate text-[11px] text-muted-foreground italic">Pin a listing…</span>
      )}
    </div>
  );
  return (
    <div className="mb-2 rounded-xl border border-accent/40 bg-accent/5 p-2 space-y-2" data-testid="chat-compare-tray">
      <div className="flex items-center gap-2">
        <Slot s="A" item={a} />
        <span className="font-display font-bold text-[10px] text-muted-foreground shrink-0">VS</span>
        <Slot s="B" item={b} />
      </div>
      {typeMismatch && (
        <p className="text-[10px] text-amber-500 text-center" data-testid="chat-compare-mismatch">Pin two homes or two vehicles to compare.</p>
      )}
      <div className="flex items-center gap-2">
        <button type="button" disabled={!bothSame} onClick={onCompare} data-testid="chat-compare-ab"
          className="flex-1 inline-flex items-center justify-center gap-1.5 text-xs font-mono-data uppercase tracking-wider px-3 py-1.5 rounded-full border border-accent/40 text-accent hover:bg-accent/10 transition-colors disabled:opacity-50 disabled:cursor-not-allowed">
          <GitCompareArrows className="w-3.5 h-3.5" /> Compare A vs B
        </button>
        <button type="button" onClick={clearPins} data-testid="chat-clear-pins" className="text-[10px] font-mono-data uppercase tracking-wider text-muted-foreground hover:text-foreground px-2">Clear</button>
      </div>
    </div>
  );
}

export default function VaelithChat({ variant = "floating", onNavigate }) {
  const {
    messages, sending, sendMessage, conversations, activeId,
    selectConversation, newChat, deleteConversation, loadingConv,
    renameConversation, pinConversation, pins, pinToSlot, clearPin, clearPins,
  } = useAnalyst() || {};
  const { user } = useAuth();
  const [input, setInput] = useState("");
  const [showHistory, setShowHistory] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [editTitle, setEditTitle] = useState("");
  const scrollRef = useRef(null);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages, sending]);

  const send = (text) => {
    const q = (text ?? input).trim();
    if (!q) return;
    setInput("");
    sendMessage(q);
  };

  const containerH = "flex-1 min-h-0";

  return (
    <div className="flex flex-col flex-1 min-h-0" data-testid={`vaelith-chat-${variant}`}>
      {/* Header row: history toggle + new chat */}
      <div className="flex items-center gap-2 px-3 py-2 border-b border-border shrink-0">
        <button
          onClick={() => setShowHistory((s) => !s)} data-testid="chat-history-toggle"
          className={`inline-flex items-center gap-1.5 text-xs font-mono-data uppercase tracking-wider px-2.5 py-1.5 rounded-full border transition-colors ${showHistory ? "bg-accent/15 text-accent border-accent/30" : "border-border text-muted-foreground hover:text-foreground"}`}
        >
          {showHistory ? <ChevronLeft className="w-3.5 h-3.5" /> : <MessagesSquare className="w-3.5 h-3.5" />}
          {showHistory ? "Back" : `History${conversations?.length ? ` (${conversations.length})` : ""}`}
        </button>
        <div className="flex-1" />
        <button onClick={() => { newChat(); setShowHistory(false); }} data-testid="chat-new"
          className="inline-flex items-center gap-1.5 text-xs font-mono-data uppercase tracking-wider px-2.5 py-1.5 rounded-full border border-border text-muted-foreground hover:text-foreground hover:border-accent/50 transition-colors">
          <Plus className="w-3.5 h-3.5" /> New
        </button>
      </div>

      {showHistory ? (
        /* -------- CONVERSATION HISTORY -------- */
        <div className={`${containerH} overflow-y-auto p-2 space-y-1`} data-testid="chat-history-list">
          {(!conversations || conversations.length === 0) && (
            <p className="text-sm text-muted-foreground text-center py-8">No saved conversations yet.</p>
          )}
          {conversations?.map((c) => (
            <div key={c.id}
              className={`group flex items-center gap-2 rounded-xl px-3 py-2.5 transition-colors ${c.id === activeId ? "bg-accent/10 border border-accent/30" : "hover:bg-secondary border border-transparent"}`}
              data-testid="chat-history-item">
              <button onClick={(ev) => { ev.stopPropagation(); pinConversation(c.id, !c.pinned); }}
                data-testid="chat-history-pin" title={c.pinned ? "Unpin" : "Pin"}
                className={`shrink-0 transition-colors ${c.pinned ? "text-accent" : "text-muted-foreground opacity-0 group-hover:opacity-100 hover:text-foreground"}`}>
                {c.pinned ? <Pin className="w-3.5 h-3.5 fill-current" /> : <Pin className="w-3.5 h-3.5" />}
              </button>
              {editingId === c.id ? (
                <input
                  autoFocus value={editTitle} onChange={(e) => setEditTitle(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") { renameConversation(c.id, editTitle); setEditingId(null); }
                    if (e.key === "Escape") setEditingId(null);
                  }}
                  onBlur={() => { renameConversation(c.id, editTitle); setEditingId(null); }}
                  data-testid="chat-history-rename-input"
                  className="flex-1 bg-transparent border-b border-accent/50 text-sm focus:outline-none"
                />
              ) : (
                <span className="text-sm truncate flex-1 cursor-pointer"
                  onClick={() => { selectConversation(c.id); setShowHistory(false); }}>{c.title}</span>
              )}
              <button onClick={(ev) => { ev.stopPropagation(); setEditingId(c.id); setEditTitle(c.title); }}
                data-testid="chat-history-rename"
                className="opacity-0 group-hover:opacity-100 text-muted-foreground hover:text-foreground transition-opacity shrink-0">
                <Pencil className="w-3.5 h-3.5" />
              </button>
              <button onClick={(ev) => { ev.stopPropagation(); deleteConversation(c.id); }}
                data-testid="chat-history-delete"
                className="opacity-0 group-hover:opacity-100 text-muted-foreground hover:text-rose-500 transition-opacity shrink-0">
                <Trash2 className="w-3.5 h-3.5" />
              </button>
            </div>
          ))}
        </div>
      ) : (
        <>
          {/* -------- THREAD -------- */}
          <div ref={scrollRef} className={`${containerH} overflow-y-auto p-4 space-y-3`} data-testid="chat-thread" role="log" aria-live="polite">
            {messages.length === 0 && !loadingConv && (
              <div>
                <div className="bg-secondary text-secondary-foreground text-sm rounded-2xl px-3.5 py-3 leading-relaxed" data-testid="chat-welcome">
                  Welcome to Vaelith. I'm your AI concierge for luxury real estate and exotic vehicles. Ask me to find
                  listings, read a market, or build a negotiation strategy — I answer only from verified data. What are you looking for?
                </div>
                <div className="font-mono-data text-[10px] uppercase tracking-wider text-muted-foreground mt-4 mb-2">Try asking</div>
                <div className="flex flex-wrap gap-2">
                  {SUGGESTED.map((s) => (
                    <button key={s} onClick={() => send(s)} data-testid="chat-suggestion"
                      className="text-xs border border-border rounded-full px-3 py-1.5 text-muted-foreground hover:text-foreground hover:border-accent/50 transition-colors">
                      {s}
                    </button>
                  ))}
                </div>
              </div>
            )}
            {loadingConv && <div className="flex items-center gap-2 text-xs text-muted-foreground animate-pulse py-2"><Loader2 className="w-4 h-4 animate-spin" /> Loading conversation…</div>}
            {messages.map((m, i) => (
              <div key={i} className={m.role === "user" ? "flex justify-end" : ""}>
                {m.role === "user" ? (
                  <div className="bg-accent/15 border border-accent/30 text-sm rounded-2xl rounded-br-sm px-3.5 py-2.5 max-w-[85%]" data-testid="chat-user-msg">{m.text}</div>
                ) : (
                  <div className="max-w-full" data-testid="chat-ai-msg">
                    <div className={`text-sm rounded-2xl rounded-bl-sm px-3.5 py-3 leading-relaxed whitespace-pre-wrap ${m.kind === "off_topic" ? "bg-rose-500/10 border border-rose-500/30 text-rose-600 dark:text-rose-400" : "bg-secondary text-secondary-foreground"}`}>
                      {m.text}
                    </div>
                    {m.kind === "negotiate_locked" && (
                      <Link to="/pricing" onClick={onNavigate} data-testid="chat-upgrade">
                        <Button size="sm" className="rounded-full mt-2"><Lock className="w-3.5 h-3.5 mr-1.5" />Upgrade to unlock</Button>
                      </Link>
                    )}
                    {m.results && <ResultChips results={m.results} onNavigate={onNavigate}
                      pins={pins}
                      onPin={(slot, item) => pinToSlot(slot, item)} />}
                    {m.results?.methodology && (
                      <div className="mt-2 flex items-start gap-1.5 text-[11px] text-muted-foreground/90 border-l-2 border-accent/40 pl-2.5" data-testid="chat-ranking-methodology">
                        <ListOrdered className="w-3.5 h-3.5 text-accent shrink-0 mt-0.5" />
                        <span>{m.results.methodology}</span>
                      </div>
                    )}
                    {m.results && m.results.listings?.length > 0 && m.search && <AlertButton search={m.search} />}
                    {m.comparison && <ComparisonCard cmp={m.comparison} />}
                    {m.negotiation && <NegotiationCard neg={m.negotiation} />}
                  </div>
                )}
              </div>
            ))}
            {sending && (
              <div className="flex items-center gap-2 font-mono-data text-xs text-accent animate-pulse" data-testid="chat-thinking">
                <Loader2 className="w-4 h-4 animate-spin" /> Thinking…
              </div>
            )}
          </div>

          {/* -------- INPUT -------- */}
          <div className="p-3 border-t border-border shrink-0">
            <CompareTray
              pins={pins} clearPin={clearPin} clearPins={clearPins}
              onCompare={() => pins?.A?.item_id && pins?.B?.item_id && sendMessage(
                `Compare ${pins.A.title || "A"} vs ${pins.B.title || "B"}`,
                undefined, { compare: { a_id: pins.A.item_id, b_id: pins.B.item_id } })}
            />
            <div className="flex items-center gap-2 border border-border rounded-full pl-4 pr-1.5 py-1 focus-within:border-accent/50 transition-colors">
              <input
                value={input} onChange={(e) => setInput(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && send()}
                placeholder={user ? "Ask Vaelith AI about homes or vehicles…" : "Sign in to ask…"}
                data-testid="chat-input"
                disabled={!user}
                className="flex-1 bg-transparent text-sm focus:outline-none disabled:opacity-60"
              />
              <Button size="icon" className="rounded-full h-8 w-8 shrink-0" disabled={sending || !input.trim() || !user} onClick={() => send()} data-testid="chat-send">
                <Send className="w-4 h-4" />
              </Button>
            </div>
            <div className="flex items-center justify-center gap-1.5 mt-2 font-mono-data text-[9px] uppercase tracking-wider text-muted-foreground/60">
              <Sparkles className="w-3 h-3" /> Grounded in verified data — never fabricated
            </div>
          </div>
        </>
      )}
    </div>
  );
}
